import { Alert, AlertTitle, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import React, { useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks"
import { toggleEscalatedNoteAddModal } from "../../../../../app/slices/VerificationModuleSlice";
import { EscalatedNotePostCommand, VerificationSaveCommand } from "../../../../../services/verifyService/commands";
import { Note } from "../../../../../services/verifyService/model/verification/Note";
import { addNote } from "../../../../../services/verifyService/model/verification/VerificationSlice";
import { useSaveEscalatedNoteMutation, useSaveVerificationMutation } from "../../../../../services/verifyService/verify-api";

const ESCALATION_REASONS = [
    'Need updated insurance',
    'Need posting sheet',
    'Need updated posting sheet with new stay type',
    'Need correct cpt code-invalid',
    'Need updated demographics',
    'Pt paid doctor',
    'Removed cpt code',
    'Added cpt code',
    'Need EPR- patient has called in requesting',
    'Other'
] as const;

function EscalatedNoteAdd(){
    const dispatch = useAppDispatch();
    const modalOpen = useAppSelector((state) => state.verificationModule.EscalatedNoteAddModal);
    const verification = useAppSelector((state) => state.verification.verification);
    const [escalationReason,setEscalationReason] = useState('');
    const [otherReason, setOtherReason] = useState('');
    const [saveEscalatedNoteToServer] = useSaveEscalatedNoteMutation();
    const [saveVerification] = useSaveVerificationMutation();
    const [isSaving, setIsSaving] = useState(false);

    const handleReasonChange = (event: SelectChangeEvent<string>) => {
        setEscalationReason(event.target.value);
        if (event.target.value !== 'Other') {
            setOtherReason('');
        }
    };

    const getEffectiveReason = () => {
        return escalationReason === 'Other' ? otherReason : escalationReason;
    };
    
    const saveNote = async () => {
        setIsSaving(true);
        try {
            const effectiveReason = getEffectiveReason();
            let newNote = new Note();
            newNote.noteText = `FACILITY REQUEST - Reason: ${effectiveReason}`; 
            newNote.changeBy='You'; 
            newNote.changeDate = new Date();
            dispatch(addNote(newNote));
            // First save the escalated note event, then save verification to avoid race condition
            await saveEscalatedNoteToServer(new EscalatedNotePostCommand(verification.id, effectiveReason)).unwrap();
            // Save the verification to persist the isEscalated toggle state
            await saveVerification(new VerificationSaveCommand(verification)).unwrap();
            setEscalationReason('');
            setOtherReason('');
            dispatch(toggleEscalatedNoteAddModal());
        } catch (error) {
            console.error('Error saving facility request:', error);
            // TODO: Add user-facing error notification (e.g., toast/snackbar)
            alert('Failed to save facility request. Please try again.');
        } finally {
            setIsSaving(false);
        }
    }

    const handleClose = () => {
        if (isSaving) return; // Prevent closing during save
        setEscalationReason('');
        setOtherReason('');
        dispatch(toggleEscalatedNoteAddModal());
    }

    const isFormValid = () => {
        return escalationReason === 'Other' 
            ? otherReason.trim() !== '' 
            : escalationReason !== '';
    };

    return <React.Fragment>
        <Dialog 
            open={modalOpen} 
            onClose={isSaving ? undefined : handleClose} 
            maxWidth="md" 
            fullWidth
            disableEscapeKeyDown={isSaving}
        >
            <DialogTitle>Facility Request</DialogTitle>
            <DialogContent>
                <Alert severity="info" sx={{marginBottom:'20px'}}>
                    <AlertTitle>Instructions</AlertTitle>
                    Please select a reason for this facility request.
                </Alert>
                <FormControl fullWidth sx={{marginTop:'10px', marginBottom:'20px'}}>
                    <InputLabel id="escalation-reason-label" shrink>Reason for Request *</InputLabel>
                    <Select
                        labelId="escalation-reason-label"
                        value={escalationReason}
                        onChange={handleReasonChange}
                        label="Reason for Request *"
                        displayEmpty
                        notched
                    >
                        <MenuItem value="" disabled>
                            <em>Select a reason...</em>
                        </MenuItem>
                        {ESCALATION_REASONS.map((reason) => (
                            <MenuItem key={reason} value={reason}>
                                {reason}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {escalationReason === 'Other' && (
                    <TextField 
                        label='Please specify reason' 
                        value={otherReason} 
                        onChange={(e)=>setOtherReason(e.target.value)} 
                        required
                        fullWidth
                        InputLabelProps={{shrink:true}} 
                        placeholder="Enter your reason..."
                        inputProps={{ maxLength: 500 }}
                        helperText={`${otherReason.length}/500 characters`}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSaving}>Cancel</Button>
                <Button 
                    onClick={saveNote}
                    disabled={!isFormValid() || isSaving}
                    variant="contained"
                >
                    {isSaving ? 'Saving...' : 'Submit'}
                </Button>
            </DialogActions>
        </Dialog>
    </React.Fragment>
}

export {EscalatedNoteAdd}
